import { Zip, ZipPassThrough } from "fflate";
import streamSaver from "streamsaver";
import { getRetouchAdminToken, getRetouchReviewApiBase } from "./retouchReviewApi";

let streamSaverConfigured = false;

const MAX_FETCH_ATTEMPTS = 3;
const RETRY_DELAY_MS = 400;
const FAILED_FILES_REPORT_NAME = "_download-failures.txt";

export const CANCELLED_MESSAGE = "CANCELLED";
export const UNSUPPORTED_MESSAGE = "STREAMING_ZIP_UNSUPPORTED";

function getPublicUrlPrefix() {
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    if (!base || base === ".") return "";
    return base;
}

function configureStreamSaver() {
    if (streamSaverConfigured) return;
    streamSaver.mitm = `${window.location.origin}${getPublicUrlPrefix()}/mitm.html`;
    streamSaverConfigured = true;
}

export function isRetouchStreamingZipSupported() {
    if (typeof window === "undefined") return false;
    if (window.isSecureContext !== true) return false;
    if (
        typeof ReadableStream === "undefined" ||
        typeof WritableStream === "undefined" ||
        typeof TransformStream === "undefined" ||
        typeof MessageChannel === "undefined"
    ) {
        return false;
    }
    if (!navigator.serviceWorker) return false;
    if (window.safari || window.WebKitPoint) return false;
    return true;
}

export function safeRetouchZipFilename(name) {
    const cleaned = String(name || "retouch")
        .replace(/[^\w\u4e00-\u9fff.\- ()]+/g, "_")
        .trim();
    return `${(cleaned || "retouch").slice(0, 80)}.zip`;
}

function uniqueRetouchZipPath(path, usedPaths) {
    const base = String(path || "photo");
    if (!usedPaths.has(base)) {
        usedPaths.add(base);
        return base;
    }
    const dotIndex = base.lastIndexOf(".");
    const stem = dotIndex > 0 ? base.slice(0, dotIndex) : base;
    const ext = dotIndex > 0 ? base.slice(dotIndex) : "";
    let index = 2;
    let candidate = `${stem} (${index})${ext}`;
    while (usedPaths.has(candidate)) {
        index += 1;
        candidate = `${stem} (${index})${ext}`;
    }
    usedPaths.add(candidate);
    return candidate;
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function retouchDownloadHeaders(token) {
    return token ? { "X-Retouch-Token": token } : { Authorization: `Bearer ${getRetouchAdminToken()}` };
}

function latestRetouchVersion(photo) {
    return photo.latestVersion || Object.keys(photo.versions || {})[0] || "";
}

function retouchDownloadUrl(photo, token) {
    const base = getRetouchReviewApiBase();
    const params = new URLSearchParams({ photoId: photo.id, version: latestRetouchVersion(photo) });
    const path = token
        ? `/api/retouch-review/download/${encodeURIComponent(token)}?${params.toString()}`
        : `/api/retouch-review/download?${params.toString()}`;
    return `${base}${path}`;
}

function formatFailureReport(failed, zipName) {
    const lines = [
        `Failed files in "${zipName || "retouch"}": ${failed.length}`,
        `Generated: ${new Date().toISOString()}`,
        "",
    ];
    failed.forEach((item, index) => {
        lines.push(`${index + 1}. ${item.name || "unknown"}`);
        lines.push(`   ${item.error || "Download failed"}`);
    });
    return `${lines.join("\n")}\n`;
}

function addStringToZip(zip, zipPath, text) {
    const entry = new ZipPassThrough(zipPath);
    zip.add(entry);
    entry.push(new TextEncoder().encode(text), true);
}

async function fetchRetouchDownload(url, headers, signal) {
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
        if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
        try {
            const response = await fetch(url, { headers, cache: "no-store", signal });
            if (response.ok) return response;
            await response.body?.cancel?.();
            const error = new Error(`HTTP ${response.status}`);
            if (response.status < 500) throw error;
            lastError = error;
        } catch (error) {
            if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
            lastError = error;
        }
        if (attempt < MAX_FETCH_ATTEMPTS) await delay(RETRY_DELAY_MS * attempt);
    }
    throw lastError || new Error("Download failed");
}

async function pumpResponseToZip(response, zipEntry, signal, checkFatal) {
    if (!response.body) {
        checkFatal?.();
        const buffer = new Uint8Array(await response.arrayBuffer());
        checkFatal?.();
        zipEntry.push(buffer, true);
        checkFatal?.();
        return;
    }

    const reader = response.body.getReader();
    try {
        while (true) {
            if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
            checkFatal?.();
            const { done, value } = await reader.read();
            if (done) {
                zipEntry.push(new Uint8Array(0), true);
                break;
            }
            zipEntry.push(value, false);
            checkFatal?.();
        }
    } finally {
        reader.releaseLock();
    }
}

async function addPhotoToZip(zip, zipPath, photo, token, signal, checkFatal) {
    checkFatal?.();
    const response = await fetchRetouchDownload(retouchDownloadUrl(photo, token), retouchDownloadHeaders(token), signal);
    checkFatal?.();
    const entry = new ZipPassThrough(zipPath);
    zip.add(entry);
    try {
        await pumpResponseToZip(response, entry, signal, checkFatal);
    } catch (error) {
        error.fatalArchive = true;
        throw error;
    }
}

/**
 * 将精修审核全部照片的最新版本边下边打包写入磁盘。
 *
 * @param {{ token?: string, photos: Array, zipName?: string, onProgress?: Function, signal?: AbortSignal }} options
 * @returns {Promise<{ succeeded: number, failed: Array, total: number }>}
 */
export async function downloadAllRetouchPhotosAsZip({ token = "", photos = [], zipName = "retouch", onProgress, signal } = {}) {
    if (!photos.length) throw new Error("NO_FILES");
    if (!isRetouchStreamingZipSupported()) throw new Error(UNSUPPORTED_MESSAGE);

    configureStreamSaver();
    const fileStream = streamSaver.createWriteStream(safeRetouchZipFilename(zipName));
    const writer = fileStream.getWriter();

    let writeQueue = Promise.resolve();
    let zipError = null;
    let writeError = null;

    const recordWriteError = (error) => {
        if (!writeError) writeError = error;
        throw error;
    };

    const checkFatal = () => {
        if (zipError || writeError) throw zipError || writeError;
    };

    const zip = new Zip((error, chunk, final) => {
        if (error) {
            zipError = error;
            return;
        }
        if (chunk?.length) {
            writeQueue = writeQueue.then(() => writer.write(chunk)).catch(recordWriteError);
        }
        if (final) {
            writeQueue = writeQueue.then(() => writer.close()).catch(recordWriteError);
        }
    });

    const usedPaths = new Set();
    const failed = [];
    let succeeded = 0;
    const total = photos.length;

    try {
        for (let index = 0; index < total; index += 1) {
            if (signal?.aborted) throw new Error(CANCELLED_MESSAGE);
            checkFatal();

            const photo = photos[index];
            const zipPath = uniqueRetouchZipPath(photo.relativePath || `photo-${index + 1}`, usedPaths);
            onProgress?.({
                current: index + 1,
                total,
                name: photo.relativePath || "",
                failedCount: failed.length,
                phase: "fetching",
            });

            try {
                await addPhotoToZip(zip, zipPath, photo, token, signal, checkFatal);
                checkFatal();
                await writeQueue;
                checkFatal();
                succeeded += 1;
            } catch (error) {
                if (signal?.aborted) throw error;
                if (zipError || writeError || error.fatalArchive) throw zipError || writeError || error;
                failed.push({
                    name: photo.relativePath || zipPath,
                    error: error.message || "Download failed",
                });
                onProgress?.({
                    current: index + 1,
                    total,
                    name: photo.relativePath || "",
                    failedCount: failed.length,
                    phase: "failed",
                });
            }
        }

        if (failed.length) {
            addStringToZip(zip, FAILED_FILES_REPORT_NAME, formatFailureReport(failed, zipName));
            checkFatal();
            await writeQueue;
            checkFatal();
        }

        zip.end();
        await writeQueue;
        checkFatal();

        onProgress?.({
            current: total,
            total,
            name: "",
            done: true,
            failedCount: failed.length,
            succeededCount: succeeded,
        });

        return { succeeded, failed, total };
    } catch (error) {
        zip.terminate();
        try {
            await writer.abort(error);
        } catch {
            /* ignore */
        }
        throw error;
    }
}
